import { TaskStatus, WritableTaskStatus } from '../constants/task';

const BACKLOG_FAMILY = [TaskStatus.backlog, TaskStatus.newBacklog, TaskStatus.reopened] as const;
type BacklogFamily = typeof BACKLOG_FAMILY;

/**
 * Result of one progress query value.
 * 'all' means the param was empty.
 * 'one' is exact equality.
 * 'family' is exactly the three stored backlog-family values, in enum order.
 */
export type ProgressMatch =
  | { readonly kind: 'all' }
  | { readonly kind: 'one'; readonly status: TaskStatus }
  | { readonly kind: 'family'; readonly statuses: BacklogFamily };

export interface ProgressOption {
  readonly labelKey: string;
  readonly progress: TaskStatus;
}

export interface StatusChoice {
  /** Customer-facing i18n key. */
  readonly labelKey: string;
  /** Legal write. Never backlog. Flag off, never newBacklog. */
  readonly submit: WritableTaskStatus;
}

export interface TaskStatusPolicy {
  readonly separated: boolean;
  progressMatch(requested: TaskStatus | null | undefined): ProgressMatch;
  filterOptions(): readonly ProgressOption[];
  labelKey(status: TaskStatus): string;
  /**
   * current null: bulk union, do not drop the collapsed family choice.
   * current set: drop the collapsed family choice when current is already in the family.
   * Preserves target order. The collapsed choice replaces the first family target.
   */
  transitionChoices(current: TaskStatus | null, targets: readonly TaskStatus[]): readonly StatusChoice[];
  /** Flag off, a family current plus a family submit returns current. Otherwise returns submit. */
  resolveSubmit(current: TaskStatus, submit: TaskStatus): TaskStatus;
  /**
   * newBacklog and reopened: flag off, or flag on with no configured row, returns backlog.
   * Every other status returns itself.
   */
  outboundKey(status: TaskStatus, configured: ReadonlySet<TaskStatus>): TaskStatus;
  /** Hides newBacklog and reopened mapping rows. The backlog row is the one Backlog / Reopened choice. */
  showMappingRow(status: TaskStatus): boolean;
}

function isBacklogFamilyStatus(status: TaskStatus): boolean {
  return (BACKLOG_FAMILY as readonly TaskStatus[]).includes(status);
}

/** Flag-on key per stored status. Flag off, labelKey collapses the family onto the backlog key. */
export const TaskStatusName: Record<TaskStatus, string> = Object.freeze({
  [TaskStatus.backlog]: 'label_task_status_backlog',
  [TaskStatus.newBacklog]: 'label_task_status_newBacklog',
  [TaskStatus.reopened]: 'label_task_status_reopened',
  [TaskStatus.toDo]: 'label_task_status_toDo',
  [TaskStatus.inProgress]: 'label_task_status_inProgress',
  [TaskStatus.requiresReview]: 'label_task_status_requiresReview',
  [TaskStatus.reviewInProgress]: 'label_task_status_reviewInProgress',
  [TaskStatus.done]: 'label_task_status_done',
});

const TASK_STATUS_WEBHOOK_TEXT: Record<TaskStatus, string> = Object.freeze({
  [TaskStatus.backlog]: 'Backlog',
  [TaskStatus.newBacklog]: 'Backlog',
  [TaskStatus.reopened]: 'Reopened',
  [TaskStatus.done]: 'Done',
  [TaskStatus.toDo]: 'To Do',
  [TaskStatus.inProgress]: 'In progress',
  [TaskStatus.requiresReview]: 'Requires review',
  [TaskStatus.reviewInProgress]: 'Review in progress',
});

export function taskStatusPolicy(separated: boolean): TaskStatusPolicy {
  const labelKey = (status: TaskStatus): string => {
    if (!separated && isBacklogFamilyStatus(status)) {
      return TaskStatusName[TaskStatus.backlog];
    }
    return TaskStatusName[status];
  };

  return {
    separated,
    labelKey,
    progressMatch(requested: TaskStatus | null | undefined): ProgressMatch {
      if (requested === null || requested === undefined || requested.length === 0) {
        return { kind: 'all' };
      }
      if (!separated && isBacklogFamilyStatus(requested)) {
        return { kind: 'family', statuses: BACKLOG_FAMILY };
      }
      return { kind: 'one', status: requested };
    },
    filterOptions(): readonly ProgressOption[] {
      if (separated) {
        return (Object.values(TaskStatus) as TaskStatus[]).map((status: TaskStatus): ProgressOption => ({
          labelKey: labelKey(status),
          progress: status,
        }));
      }

      const options: ProgressOption[] = [];
      let emittedFamily: boolean = false;
      for (const status of Object.values(TaskStatus) as TaskStatus[]) {
        if (isBacklogFamilyStatus(status)) {
          if (!emittedFamily) {
            options.push({ labelKey: TaskStatusName[TaskStatus.backlog], progress: TaskStatus.reopened });
            emittedFamily = true;
          }
          continue;
        }
        options.push({ labelKey: labelKey(status), progress: status });
      }
      return options;
    },
    transitionChoices(current: TaskStatus | null, targets: readonly TaskStatus[]): readonly StatusChoice[] {
      if (separated) {
        const choices: StatusChoice[] = [];
        for (const target of targets) {
          if (!isWritableTarget(target)) {
            continue;
          }
          choices.push({ labelKey: labelKey(target), submit: target });
        }
        return choices;
      }

      const dropCollapsed: boolean = current !== null && isBacklogFamilyStatus(current);
      const collapsed: StatusChoice = {
        labelKey: TaskStatusName[TaskStatus.backlog],
        submit: TaskStatus.reopened,
      };
      const choices: StatusChoice[] = [];
      let replaced: boolean = false;
      for (const target of targets) {
        if (isBacklogFamilyStatus(target)) {
          if (!dropCollapsed && !replaced) {
            choices.push(collapsed);
            replaced = true;
          }
          continue;
        }
        choices.push({ labelKey: labelKey(target), submit: target as WritableTaskStatus });
      }
      return choices;
    },
    resolveSubmit(current: TaskStatus, submit: TaskStatus): TaskStatus {
      if (!separated && isBacklogFamilyStatus(current) && isBacklogFamilyStatus(submit)) {
        return current;
      }
      return submit;
    },
    outboundKey(status: TaskStatus, configured: ReadonlySet<TaskStatus>): TaskStatus {
      if (status === TaskStatus.newBacklog || status === TaskStatus.reopened) {
        if (!separated || !configured.has(status)) {
          return TaskStatus.backlog;
        }
      }
      return status;
    },
    showMappingRow(status: TaskStatus): boolean {
      return status !== TaskStatus.newBacklog && status !== TaskStatus.reopened;
    },
  };
}

/** Webhook taskStatusString. Flag-blind. newStatus in the payload stays the stored enum. */
export function webhookStatusText(status: TaskStatus): string {
  return TASK_STATUS_WEBHOOK_TEXT[status];
}

/** Enum index + 1. Unknown status throws. */
export function comparableStatusRank(status: TaskStatus): string {
  const index: number = (Object.values(TaskStatus) as TaskStatus[]).indexOf(status);
  if (index === -1) {
    throw new Error(`Unknown task status: ${status}`);
  }
  return String(index + 1);
}

export function isNotStartedStatus(status: TaskStatus): boolean {
  return status === TaskStatus.toDo || isBacklogFamilyStatus(status);
}

export function isDeletableStatus(status: TaskStatus): boolean {
  return isBacklogFamilyStatus(status) || status === TaskStatus.toDo || status === TaskStatus.inProgress;
}

export function isWritableTarget(status: TaskStatus): status is WritableTaskStatus {
  return status !== TaskStatus.backlog;
}

/**
 * A saved backlog mapping row cannot be written. A task still in the initial
 * backlog bucket becomes newBacklog. A task that has already moved becomes reopened.
 */
export function inboundAppliedStatus(mapped: TaskStatus, current: TaskStatus): WritableTaskStatus {
  if (mapped !== TaskStatus.backlog) {
    return mapped as WritableTaskStatus;
  }
  if (current === TaskStatus.backlog || current === TaskStatus.newBacklog) {
    return TaskStatus.newBacklog;
  }
  return TaskStatus.reopened;
}
